import React, { useState, useCallback } from "react";
import {
  Box, Typography, Stack, Paper, TextField, Button, Chip,
  IconButton, Tooltip, Tabs, Tab, Alert, Select, MenuItem,
  FormControl, InputLabel, Grid, Radio, RadioGroup, FormControlLabel, Collapse,
} from "@mui/material";
import {
  Quiz as QuizIcon, Lightbulb, HowToVote, MedicalServices,
  Timer, EmojiEvents, Add, Delete, ExpandMore, ExpandLess,
  Visibility, Refresh, OpenInNew,
} from "@mui/icons-material";
import { DOMAIN, PAGE_LINKS, uid, CopyButton } from "./marketingShared";

// ─── Constants ───────────────────────────────────────────────────────────────

const OPTION_LETTERS  = ["A", "B", "C", "D", "E"];
const TIP_CATEGORIES  = ["Nutrition","Surgery","Emergency","Behavior","Pharmacology","Diagnosis","Wellness"];
const TIP_EMOJIS      = ["💡","🔬","🐾","💊","🩺","⚡","🏥","🧬","❤️","⚠️","🐕","🐈","📊","🦷","🧪"];

const COUNTDOWN_EXAMPLES = [
  { tag: "Surgery Masterclass",  title: "Advanced Small Animal Surgery Masterclass",            subtitle: "8 weeks · Expert-led · CE credits included",              emoji: "🏥", badge: "Only 30 spots",         cta: "Secure My Spot",    daysFromNow: 7  },
  { tag: "Emergency Medicine",   title: "Emergency & Critical Care Bootcamp",                   subtitle: "Intensive 4-week online program for practicing vets",     emoji: "⚡", badge: "Early bird — 20% off",  cta: "Join the Waitlist", daysFromNow: 14 },
  { tag: "Dermatology Course",   title: "Veterinary Dermatology: From Basics to Advanced",      subtitle: "Self-paced · Lifetime access · Certificate included",     emoji: "🐾", badge: "Founding member pricing",cta: "Lock In My Price",  daysFromNow: 5  },
];

const LEADERBOARD_EXAMPLES = [
  {
    tag: "Pharmacology Weekly", quiz: "Weekly Pharmacology Challenge", period: "This Week",
    entries: [
      { name: "Dr. Sara Khalil",  score: "98", flag: "🇱🇧" },
      { name: "Dr. Omar Nasser",  score: "95", flag: "🇪🇬" },
      { name: "Dr. Lina Haddad",  score: "91", flag: "🇯🇴" },
      { name: "Dr. Youssef Amin", score: "88", flag: "🇸🇦" },
      { name: "Dr. Maya Ibrahim", score: "85", flag: "🇦🇪" },
    ],
  },
  {
    tag: "Emergency — Monthly", quiz: "Emergency & Critical Care Showdown", period: "This Month",
    entries: [
      { name: "Dr. Karim Saad",  score: "100", flag: "🇪🇬" },
      { name: "Dr. Rania Mousa", score: "96",  flag: "🇸🇩" },
      { name: "Dr. Tariq Farhat",score: "93",  flag: "🇱🇧" },
      { name: "Dr. Dina Qasem",  score: "90",  flag: "🇯🇴" },
    ],
  },
  {
    tag: "All-Time Anatomy", quiz: "Veterinary Anatomy Masters", period: "All Time",
    entries: [
      { name: "Dr. Hana Zidan",  score: "99", flag: "🇸🇦" },
      { name: "Dr. Bilal Ramzi", score: "97", flag: "🇦🇪" },
      { name: "Dr. Nour Sabbagh",score: "95", flag: "🇸🇾" },
      { name: "Dr. Faris Khalaf",score: "92", flag: "🇮🇶" },
      { name: "Dr. Aya Mansour", score: "90", flag: "🇲🇦" },
    ],
  },
];

// ─── Types ───────────────────────────────────────────────────────────────────

interface QuizOption    { id: string; text: string; }
interface PollOption    { id: string; text: string; votes: string; }
interface LbEntry       { id: string; name: string; score: string; flag: string; }
interface QuizQuestion  { id: string; text: string; explanation: string; options: QuizOption[]; correctId: string; expanded: boolean; }

// ─────────────────────────────────────────────────────────────────────────────

const AdminMarketingEngagement: React.FC = () => {
  const [tab, setTab] = useState(0);

  // ── Quiz state ────────────────────────────────────────────────────────────
  const [quizTitle,    setQuizTitle]    = useState("");
  const [quizShareUrl, setQuizShareUrl] = useState("");
  const [questions,    setQuestions]    = useState<QuizQuestion[]>([
    { id: uid(), text: "", explanation: "", correctId: "", expanded: true, options: [{ id: uid(), text: "" }, { id: uid(), text: "" }] },
  ]);
  const [quizAlert, setQuizAlert] = useState("");

  // ── Tip Card state ────────────────────────────────────────────────────────
  const [tipTitle,    setTipTitle]    = useState("");
  const [tipBody,     setTipBody]     = useState("");
  const [tipCategory, setTipCategory] = useState("Nutrition");
  const [tipStat,     setTipStat]     = useState("");
  const [tipEmoji,    setTipEmoji]    = useState("💡");
  const [tipLink,     setTipLink]     = useState("/#pricing");
  const [tipUrl,      setTipUrl]      = useState("");
  const [tipAlert,    setTipAlert]    = useState("");

  // ── Poll state ────────────────────────────────────────────────────────────
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOpts,     setPollOpts]     = useState<PollOption[]>([
    { id: uid(), text: "", votes: "50" },
    { id: uid(), text: "", votes: "30" },
  ]);
  const [pollLink,  setPollLink]  = useState("/#pricing");
  const [pollUrl,   setPollUrl]   = useState("");
  const [pollAlert, setPollAlert] = useState("");

  // ── Case state ────────────────────────────────────────────────────────────
  const [caseTitle,       setCaseTitle]       = useState("");
  const [caseScenario,    setCaseScenario]    = useState("");
  const [caseQuestion,    setCaseQuestion]    = useState("");
  const [caseOpts,        setCaseOpts]        = useState<string[]>(["", "", "", ""]);
  const [caseAnswer,      setCaseAnswer]      = useState(0);
  const [caseExplanation, setCaseExplanation] = useState("");
  const [caseLink,        setCaseLink]        = useState("/#pricing");
  const [caseUrl,         setCaseUrl]         = useState("");
  const [caseAlert,       setCaseAlert]       = useState("");

  // ── Countdown state ───────────────────────────────────────────────────────
  const [cdTitle,    setCdTitle]    = useState("");
  const [cdSubtitle, setCdSubtitle] = useState("");
  const [cdEmoji,    setCdEmoji]    = useState("🎓");
  const [cdBadge,    setCdBadge]    = useState("");
  const [cdCta,      setCdCta]      = useState("Secure My Spot");
  const [cdLink,     setCdLink]     = useState("/#pricing");
  const [cdDate,     setCdDate]     = useState("");
  const [cdUrl,      setCdUrl]      = useState("");
  const [cdAlert,    setCdAlert]    = useState("");

  // ── Leaderboard state ─────────────────────────────────────────────────────
  const [lbQuiz,    setLbQuiz]    = useState("");
  const [lbPeriod,  setLbPeriod]  = useState("This Week");
  const [lbEntries, setLbEntries] = useState<LbEntry[]>([
    { id: uid(), name: "", score: "", flag: "" },
    { id: uid(), name: "", score: "", flag: "" },
    { id: uid(), name: "", score: "", flag: "" },
  ]);
  const [lbLink,  setLbLink]  = useState("");
  const [lbUrl,   setLbUrl]   = useState("");
  const [lbAlert, setLbAlert] = useState("");

  // ── Quiz helpers ──────────────────────────────────────────────────────────
  const addQuestion    = () => setQuestions((p) => [...p, { id: uid(), text: "", explanation: "", correctId: "", expanded: true, options: [{ id: uid(), text: "" }, { id: uid(), text: "" }] }]);
  const removeQuestion = (qid: string) => setQuestions((p) => p.filter((q) => q.id !== qid));
  const toggleQuestion = (qid: string) => setQuestions((p) => p.map((q) => q.id === qid ? { ...q, expanded: !q.expanded } : q));
  const updateQuestionText = (qid: string, text: string) => setQuestions((p) => p.map((q) => q.id === qid ? { ...q, text } : q));
  const updateExplanation  = (qid: string, explanation: string) => setQuestions((p) => p.map((q) => q.id === qid ? { ...q, explanation } : q));
  const addOption    = (qid: string) => setQuestions((p) => p.map((q) => q.id === qid && q.options.length < 5 ? { ...q, options: [...q.options, { id: uid(), text: "" }] } : q));
  const removeOption = (qid: string, oid: string) => setQuestions((p) => p.map((q) => q.id === qid && q.options.length > 2 ? { ...q, options: q.options.filter((o) => o.id !== oid), correctId: q.correctId === oid ? "" : q.correctId } : q));
  const updateOptionText = (qid: string, oid: string, text: string) => setQuestions((p) => p.map((q) => q.id === qid ? { ...q, options: q.options.map((o) => o.id === oid ? { ...o, text } : o) } : q));
  const setCorrect   = (qid: string, oid: string) => setQuestions((p) => p.map((q) => q.id === qid ? { ...q, correctId: oid } : q));

  const buildQuizShareUrl = () => {
    if (!quizTitle.trim() || questions.some((q) => !q.text.trim() || q.options.some((o) => !o.text.trim()) || !q.correctId)) return "";
    try {
      return `${DOMAIN}/quiz?data=${btoa(encodeURIComponent(JSON.stringify({ title: quizTitle, questions: questions.map((q) => ({ text: q.text, options: q.options.map((o) => o.text), correctIndex: q.options.findIndex((o) => o.id === q.correctId), ...(q.explanation.trim() ? { explanation: q.explanation.trim() } : {}) })) })))}`;
    } catch { return ""; }
  };

  const buildQuizText = useCallback(() => {
    const lines: string[] = [`📝 QUIZ: ${quizTitle || "Untitled Quiz"}\n`];
    questions.forEach((q, qi) => {
      lines.push(`Q${qi + 1}: ${q.text || "[Question]"}`);
      q.options.forEach((o, oi) => lines.push(`  ${OPTION_LETTERS[oi]}) ${o.text || "[Option]"}${o.id === q.correctId ? " ✅" : ""}`));
      lines.push("");
    });
    lines.push("#DrPaw #VetQuiz #VetEducation #VeterinaryLife");
    return lines.join("\n");
  }, [quizTitle, questions]);

  const handlePreviewQuiz = () => {
    if (!quizTitle.trim()) { setQuizAlert("Add a quiz title first."); return; }
    if (questions.some((q) => !q.text.trim() || q.options.some((o) => !o.text.trim()) || !q.correctId)) { setQuizAlert("Fill in all questions, options and mark the correct answer."); return; }
    setQuizAlert(""); setQuizShareUrl(buildQuizShareUrl());
  };

  const handleClearQuiz = () => { setQuizTitle(""); setQuestions([{ id: uid(), text: "", explanation: "", correctId: "", expanded: true, options: [{ id: uid(), text: "" }, { id: uid(), text: "" }] }]); setQuizShareUrl(""); setQuizAlert(""); };

  // ── Tip helpers ───────────────────────────────────────────────────────────
  const handleGenerateTip = () => {
    if (!tipTitle.trim()) { setTipAlert("Add a tip title first."); return; }
    const params = new URLSearchParams({ title: tipTitle.trim(), link: `${DOMAIN}${tipLink}` });
    if (tipBody.trim())  params.set("body",     tipBody.trim());
    if (tipCategory)     params.set("category", tipCategory);
    if (tipStat.trim())  params.set("stat",     tipStat.trim());
    if (tipEmoji.trim()) params.set("emoji",    tipEmoji.trim());
    setTipUrl(`${DOMAIN}/tip?${params.toString()}`); setTipAlert("");
  };

  // ── Poll helpers ──────────────────────────────────────────────────────────
  const addPollOption    = () => { if (pollOpts.length >= 4) return; setPollOpts((p) => [...p, { id: uid(), text: "", votes: "15" }]); };
  const removePollOption = (id: string) => { if (pollOpts.length <= 2) return; setPollOpts((p) => p.filter((o) => o.id !== id)); };
  const updatePollOption = (id: string, field: keyof PollOption, value: string) => setPollOpts((p) => p.map((o) => o.id === id ? { ...o, [field]: value } : o));

  const handleGeneratePoll = () => {
    if (!pollQuestion.trim())                               { setPollAlert("Add a question first."); return; }
    if (pollOpts.filter((o) => o.text.trim()).length < 2)   { setPollAlert("Add at least 2 options."); return; }
    const keys  = ["a","b","c","d"] as const;
    const vkeys = ["va","vb","vc","vd"] as const;
    const params = new URLSearchParams({ q: pollQuestion.trim(), link: `${DOMAIN}${pollLink}` });
    pollOpts.filter((o) => o.text.trim()).forEach((opt, i) => { params.set(keys[i], opt.text.trim()); if (opt.votes) params.set(vkeys[i], opt.votes); });
    setPollUrl(`${DOMAIN}/poll?${params.toString()}`); setPollAlert("");
  };

  // ── Case helpers ──────────────────────────────────────────────────────────
  const updateCaseOpt = (i: number, val: string) => setCaseOpts((p) => p.map((o, idx) => idx === i ? val : o));

  const handleGenerateCase = () => {
    if (!caseTitle.trim())    { setCaseAlert("Add a case title."); return; }
    if (!caseScenario.trim()) { setCaseAlert("Add the patient scenario."); return; }
    if (!caseQuestion.trim()) { setCaseAlert("Add the diagnostic question."); return; }
    if (caseOpts.filter((o) => o.trim()).length < 2) { setCaseAlert("Add at least 2 answer options."); return; }
    if (!caseExplanation.trim()) { setCaseAlert("Add a clinical explanation."); return; }
    const params = new URLSearchParams({ title: caseTitle.trim(), scenario: caseScenario.trim(), question: caseQuestion.trim(), answer: String(caseAnswer), explanation: caseExplanation.trim(), link: `${DOMAIN}${caseLink}` });
    caseOpts.forEach((opt, i) => { if (opt.trim()) params.set(["a","b","c","d"][i], opt.trim()); });
    setCaseUrl(`${DOMAIN}/case?${params.toString()}`); setCaseAlert("");
  };

  // ── Countdown helpers ─────────────────────────────────────────────────────
  const handleLoadCountdownExample = (ex: typeof COUNTDOWN_EXAMPLES[0]) => {
    const d = new Date(); d.setDate(d.getDate() + ex.daysFromNow); d.setHours(10, 0, 0, 0);
    const pad = (n: number) => String(n).padStart(2, "0");
    setCdTitle(ex.title); setCdSubtitle(ex.subtitle); setCdEmoji(ex.emoji); setCdBadge(ex.badge); setCdCta(ex.cta);
    setCdDate(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
    setCdUrl(""); setCdAlert("");
  };

  const handleGenerateCountdown = () => {
    if (!cdTitle.trim()) { setCdAlert("Add a title."); return; }
    if (!cdDate)         { setCdAlert("Set a launch date and time."); return; }
    const untilMs = new Date(cdDate).getTime();
    if (isNaN(untilMs) || untilMs <= Date.now()) { setCdAlert("Launch date must be in the future."); return; }
    const params = new URLSearchParams({ title: cdTitle.trim(), until: String(untilMs), link: `${DOMAIN}${cdLink}` });
    if (cdSubtitle.trim()) params.set("subtitle", cdSubtitle.trim());
    if (cdEmoji.trim())    params.set("emoji",    cdEmoji.trim());
    if (cdBadge.trim())    params.set("badge",    cdBadge.trim());
    if (cdCta.trim())      params.set("cta",      cdCta.trim());
    setCdUrl(`${DOMAIN}/countdown?${params.toString()}`); setCdAlert("");
  };

  // ── Leaderboard helpers ───────────────────────────────────────────────────
  const addLbEntry    = () => { if (lbEntries.length >= 10) return; setLbEntries((p) => [...p, { id: uid(), name: "", score: "", flag: "" }]); };
  const removeLbEntry = (id: string) => { if (lbEntries.length <= 2) return; setLbEntries((p) => p.filter((e) => e.id !== id)); };
  const updateLbEntry = (id: string, field: keyof LbEntry, value: string) => setLbEntries((p) => p.map((e) => e.id === id ? { ...e, [field]: value } : e));

  const handleLoadLeaderboardExample = (ex: typeof LEADERBOARD_EXAMPLES[0]) => {
    setLbQuiz(ex.quiz); setLbPeriod(ex.period);
    setLbEntries(ex.entries.map((e) => ({ id: uid(), name: e.name, score: e.score, flag: e.flag })));
    setLbUrl(""); setLbLink(""); setLbAlert("");
  };

  const handleGenerateLeaderboard = () => {
    if (!lbQuiz.trim()) { setLbAlert("Add a quiz title."); return; }
    const validEntries = lbEntries.filter((e) => e.name.trim() && e.score.trim());
    if (validEntries.length < 2) { setLbAlert("Add at least 2 entries with name and score."); return; }
    const params = new URLSearchParams({
      quiz:    lbQuiz.trim(),
      entries: btoa(JSON.stringify(validEntries.map((e) => ({ n: e.name.trim(), s: Number(e.score), ...(e.flag.trim() ? { c: e.flag.trim() } : {}) })))),
      period:  lbPeriod,
    });
    if (lbLink.trim()) params.set("link", lbLink.trim());
    setLbUrl(`${DOMAIN}/leaderboard?${params.toString()}`); setLbAlert("");
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>

      {/* ── Page Hero ──────────────────────────────────────────────────────── */}
      <Box sx={{
        mb: 4, p: { xs: 2.5, md: 3.5 }, borderRadius: "20px",
        background: "linear-gradient(135deg,#5B21B6 0%,#7C3AED 55%,#8B5CF6 100%)",
        boxShadow: "0 8px 32px rgba(124,58,237,.3)",
      }}>
        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: "rgba(255,255,255,.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <EmojiEvents sx={{ color: "#fff", fontSize: "1.6rem" }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#fff", letterSpacing: "-0.02em" }}>
                Engagement Tools
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,.7)", mt: 0.3 }}>
                Build quizzes, tip cards, polls, clinical cases, countdown timers, and leaderboards
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {["6 Tools", "Shareable Links", "Interactive Content"].map((t) => (
              <Chip key={t} label={t} size="small" sx={{ bgcolor: "rgba(255,255,255,.15)", color: "#fff", fontWeight: 600, fontSize: ".72rem" }} />
            ))}
          </Stack>
        </Stack>
      </Box>

      {/* ── Tabs ───────────────────────────────────────────────────────────── */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 3,
          borderBottom: "1px solid #E8EDF5",
          "& .MuiTab-root": { fontWeight: 600, textTransform: "none", fontSize: ".88rem", minHeight: 48 },
          "& .Mui-selected": { color: "#7C3AED" },
          "& .MuiTabs-indicator": { bgcolor: "#7C3AED", height: 3, borderRadius: "3px 3px 0 0" },
        }}
      >
        <Tab icon={<QuizIcon fontSize="small" />}         iconPosition="start" label="Quiz Builder"  />
        <Tab icon={<Lightbulb fontSize="small" />}        iconPosition="start" label="Tip Cards"     />
        <Tab icon={<HowToVote fontSize="small" />}        iconPosition="start" label="Poll Builder"  />
        <Tab icon={<MedicalServices fontSize="small" />}  iconPosition="start" label="Case Builder"  />
        <Tab icon={<Timer fontSize="small" />}            iconPosition="start" label="Countdown"     />
        <Tab icon={<EmojiEvents fontSize="small" />}      iconPosition="start" label="Leaderboard"   />
      </Tabs>

      {/* ── TAB 0: Quiz Builder ────────────────────────────────────────────── */}
      {tab === 0 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", mb: 3 }}>
              <Box sx={{ px: 2.5, py: 2, bgcolor: "#1E40AF" }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <QuizIcon sx={{ color: "#fff" }} />
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Quiz Builder</Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,.6)" }}>Build dynamic quizzes to share with your followers</Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                {quizAlert && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setQuizAlert("")}>{quizAlert}</Alert>}
                <TextField label="Quiz Title" placeholder="e.g. Test Your Veterinary Knowledge!" value={quizTitle} onChange={(e) => setQuizTitle(e.target.value)} fullWidth size="small" />
              </Box>
            </Paper>

            {questions.map((q, qi) => (
              <Paper key={q.id} sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,.05)", mb: 2 }}>
                <Box sx={{ px: 2, py: 1.5, bgcolor: "#EEF2FF", display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }} onClick={() => toggleQuestion(q.id)}>
                  <Box sx={{ width: 26, height: 26, borderRadius: "8px", bgcolor: "#1E40AF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: ".78rem" }}>Q{qi + 1}</Typography>
                  </Box>
                  <Typography noWrap sx={{ flex: 1, fontWeight: 600, fontSize: ".88rem", color: "#0A1628" }}>{q.text || "New question..."}</Typography>
                  <Stack direction="row" spacing={0.5}>
                    {questions.length > 1 && (
                      <Tooltip title="Delete question">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeQuestion(q.id); }} sx={{ color: "#EF4444" }}>
                          <Delete sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton size="small" sx={{ color: "#78909C" }}>
                      {q.expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                  </Stack>
                </Box>
                <Collapse in={q.expanded}>
                  <Box sx={{ p: 2 }}>
                    <TextField label="Question text" placeholder="e.g. Which vaccine protects against distemper?" value={q.text} onChange={(e) => updateQuestionText(q.id, e.target.value)} fullWidth size="small" sx={{ mb: 1.5 }} />
                    <TextField label="Expert insight (optional)" placeholder="Shown to followers after they answer — adds educational value" value={q.explanation} onChange={(e) => updateExplanation(q.id, e.target.value)} fullWidth size="small" multiline rows={2} sx={{ mb: 2 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: ".82rem", color: "#455A64", mb: 1 }}>
                      Answer Options <Typography component="span" variant="caption" sx={{ color: "#78909C" }}>(select the correct one)</Typography>
                    </Typography>
                    <RadioGroup value={q.correctId} onChange={(e) => setCorrect(q.id, e.target.value)}>
                      {q.options.map((opt, oi) => (
                        <Stack key={opt.id} direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <FormControlLabel value={opt.id} control={<Radio size="small" sx={{ color: "#78909C", "&.Mui-checked": { color: "#22C55E" } }} />} label="" sx={{ mr: 0, minWidth: 0 }} />
                          <Box sx={{ width: 26, height: 26, borderRadius: "6px", flexShrink: 0, bgcolor: opt.id === q.correctId ? "#DCFCE7" : "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Typography sx={{ fontWeight: 800, fontSize: ".78rem", color: opt.id === q.correctId ? "#16A34A" : "#64748B" }}>{OPTION_LETTERS[oi]}</Typography>
                          </Box>
                          <TextField placeholder={`Option ${OPTION_LETTERS[oi]}`} value={opt.text} onChange={(e) => updateOptionText(q.id, opt.id, e.target.value)} size="small" sx={{ flex: 1, "& .MuiOutlinedInput-root": { bgcolor: opt.id === q.correctId ? "#F0FDF4" : undefined, borderRadius: "8px" } }} />
                          {q.options.length > 2 && (
                            <IconButton size="small" onClick={() => removeOption(q.id, opt.id)} sx={{ color: "#CBD5E1", "&:hover": { color: "#EF4444" } }}>
                              <Delete sx={{ fontSize: "1rem" }} />
                            </IconButton>
                          )}
                        </Stack>
                      ))}
                    </RadioGroup>
                    {q.options.length < 5 && (
                      <Button size="small" startIcon={<Add fontSize="small" />} onClick={() => addOption(q.id)} sx={{ textTransform: "none", color: "#1E40AF", fontSize: ".8rem", mt: 0.5 }}>Add option</Button>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            ))}

            <Button variant="outlined" fullWidth startIcon={<Add />} onClick={addQuestion} sx={{ borderRadius: "12px", textTransform: "none", fontWeight: 700, borderColor: "#1E40AF", color: "#1E40AF", py: 1.2, mb: 2, "&:hover": { bgcolor: "#EEF2FF" } }}>Add Question</Button>

            <Stack direction="row" spacing={1.5} sx={{ mb: quizShareUrl ? 2 : 0 }}>
              <Button variant="contained" startIcon={<Visibility />} onClick={handlePreviewQuiz} sx={{ flex: 1, bgcolor: "#1E40AF", "&:hover": { bgcolor: "#1E3A8A" }, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>Preview & Generate Link</Button>
              <Button variant="outlined" color="error" startIcon={<Refresh />} onClick={handleClearQuiz} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}>Clear All</Button>
            </Stack>

            {quizShareUrl && (
              <Paper sx={{ borderRadius: "14px", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,.06)" }}>
                <Box sx={{ px: 2, py: 1.5, bgcolor: "#1E40AF", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".88rem" }}>Shareable Quiz Link</Typography>
                  <CopyButton text={quizShareUrl} />
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography variant="caption" sx={{ color: "#1E40AF", wordBreak: "break-all", lineHeight: 1.6 }}>{quizShareUrl}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button size="small" startIcon={<OpenInNew fontSize="small" />} onClick={() => window.open(quizShareUrl, "_blank")} sx={{ textTransform: "none", color: "#1E40AF", fontSize: ".8rem" }}>Preview page</Button>
                  </Stack>
                </Box>
              </Paper>
            )}
          </Grid>

          {/* Quiz Preview */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", position: "sticky", top: 20 }}>
              <Box sx={{ px: 2.5, py: 2, bgcolor: "#1E40AF", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Quiz Preview</Typography>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,.5)" }}>Ready-to-copy formatted quiz</Typography>
                </Box>
                <CopyButton text={buildQuizText()} />
              </Box>
              <Box sx={{ p: 2.5, maxHeight: "70vh", overflowY: "auto" }}>
                <Box sx={{ bgcolor: "#F8FAFF", borderRadius: "12px", p: 2, border: "1px solid #DDEEFF" }}>
                  <Typography sx={{ fontWeight: 800, fontSize: ".95rem", color: "#0A1628", mb: 1.5 }}>📝 {quizTitle || "Untitled Quiz"}</Typography>
                  {questions.map((q, qi) => (
                    <Box key={q.id} sx={{ mb: 2 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: ".85rem", color: "#1E40AF", mb: 0.5 }}>Q{qi + 1}: {q.text || "[Question text]"}</Typography>
                      {q.options.map((o, oi) => (
                        <Stack key={o.id} direction="row" alignItems="center" spacing={0.8} sx={{ mb: 0.4 }}>
                          <Box sx={{ width: 20, height: 20, borderRadius: "4px", bgcolor: o.id === q.correctId ? "#DCFCE7" : "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <Typography sx={{ fontSize: ".7rem", fontWeight: 800, color: o.id === q.correctId ? "#16A34A" : "#64748B" }}>{OPTION_LETTERS[oi]}</Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: o.id === q.correctId ? "#16A34A" : "#455A64", fontWeight: o.id === q.correctId ? 700 : 400 }}>
                            {o.text || "[Option]"}{o.id === q.correctId ? " ✅" : ""}
                          </Typography>
                        </Stack>
                      ))}
                    </Box>
                  ))}
                  <Typography variant="caption" sx={{ color: "#78909C" }}>#DrPaw #VetQuiz #VetEducation</Typography>
                </Box>
                <Box sx={{ mt: 2, p: 1.5, borderRadius: "10px", bgcolor: "#EEF2FF", border: "1px solid #C7D2FE" }}>
                  <Typography variant="caption" sx={{ color: "#1E40AF", fontWeight: 600 }}>
                    Tip: Use in Instagram Stories quiz sticker, or post as a carousel — one question per slide.
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ── TAB 1: Tip Card Builder ─────────────────────────────────────────── */}
      {tab === 1 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", mb: 3 }}>
              <Box sx={{ px: 2.5, py: 2, background: "linear-gradient(135deg,#1565C0,#42A5F5)" }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Lightbulb sx={{ color: "#fff" }} />
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Tip Card Builder</Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,.7)" }}>Create a beautiful tip card for Instagram Stories, WhatsApp, and feed posts</Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                {tipAlert && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setTipAlert("")}>{tipAlert}</Alert>}
                <Stack spacing={2}>
                  <TextField label="Headline / Title *" placeholder="e.g. 68% of pets are overweight — here's why" value={tipTitle} onChange={(e) => setTipTitle(e.target.value)} fullWidth size="small" />
                  <TextField label="Body text (optional)" placeholder="Supporting detail..." value={tipBody} onChange={(e) => setTipBody(e.target.value)} fullWidth size="small" multiline rows={3} />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Category</InputLabel>
                      <Select label="Category" value={tipCategory} onChange={(e) => setTipCategory(e.target.value)}>
                        {TIP_CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField label="Big stat (optional)" placeholder="e.g. 68% or 1 in 3" value={tipStat} onChange={(e) => setTipStat(e.target.value)} size="small" sx={{ flex: 1 }} />
                    <TextField label="Emoji" placeholder="💡" value={tipEmoji} onChange={(e) => setTipEmoji(e.target.value)} size="small" sx={{ width: 90 }} />
                  </Stack>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#78909C", mb: 0.8, display: "block" }}>Quick emoji pick:</Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.6}>
                      {TIP_EMOJIS.map((e) => (
                        <Box key={e} onClick={() => setTipEmoji(e)} sx={{ width: 34, height: 34, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem", cursor: "pointer", border: tipEmoji === e ? "2px solid #1565C0" : "1px solid #E8EDF5", bgcolor: tipEmoji === e ? "#EEF4FF" : "#F8FAFF", "&:hover": { borderColor: "#1565C0", bgcolor: "#EEF4FF" }, transition: "all .15s" }}>
                          {e}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                  <FormControl size="small" fullWidth>
                    <InputLabel>CTA links to</InputLabel>
                    <Select label="CTA links to" value={tipLink} onChange={(e) => setTipLink(e.target.value)}>
                      {PAGE_LINKS.map((pl) => <MenuItem key={pl.path} value={pl.path}>{pl.label} — {`${DOMAIN}${pl.path}`}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Button variant="contained" startIcon={<Visibility />} onClick={handleGenerateTip} sx={{ bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" }, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>Generate Tip Card Link</Button>
                </Stack>
              </Box>
            </Paper>
            {tipUrl && (
              <Paper sx={{ borderRadius: "14px", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,.06)" }}>
                <Box sx={{ px: 2, py: 1.5, bgcolor: "#1565C0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".88rem" }}>Shareable Tip Card Link</Typography>
                  <CopyButton text={tipUrl} />
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography variant="caption" sx={{ color: "#1565C0", wordBreak: "break-all", lineHeight: 1.6 }}>{tipUrl}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button size="small" startIcon={<OpenInNew fontSize="small" />} onClick={() => window.open(tipUrl, "_blank")} sx={{ textTransform: "none", color: "#1565C0", fontSize: ".8rem" }}>Preview page</Button>
                    <Button size="small" startIcon={<Refresh fontSize="small" />} onClick={() => { setTipUrl(""); setTipTitle(""); setTipBody(""); setTipStat(""); setTipEmoji("💡"); }} sx={{ textTransform: "none", color: "#78909C", fontSize: ".8rem" }}>Clear</Button>
                  </Stack>
                </Box>
              </Paper>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", position: "sticky", top: 20 }}>
              <Box sx={{ px: 2.5, py: 2, bgcolor: "#EEF4FF" }}>
                <Typography sx={{ fontWeight: 700, color: "#0A1628", fontSize: ".95rem" }}>📱 How to use Tip Cards</Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                {[
                  { step: "1", title: "Fill in the builder", desc: "Add a headline, optional big stat, category, emoji. The stat is shown large — great for attention-grabbing posts." },
                  { step: "2", title: "Generate the link", desc: "Click Generate — copy the link and paste it in your Instagram bio, Stories swipe-up, or WhatsApp broadcast." },
                  { step: "3", title: "Toggle Story Mode", desc: "On the tip page followers can switch to 9:16 Story Mode — perfect for screenshotting and posting to their own stories." },
                  { step: "4", title: "Use the hashtag hook", desc: "The page prompts followers to tag #DrPaw and #DrPawTips — giving you free user-generated content." },
                ].map((item) => (
                  <Box key={item.step} sx={{ display: "flex", gap: 2, mb: 2.5, "&:last-child": { mb: 0 } }}>
                    <Box sx={{ width: 26, height: 26, borderRadius: "8px", bgcolor: "#1565C0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: 0.2 }}>
                      <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: ".78rem" }}>{item.step}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: ".88rem", color: "#0A1628", mb: 0.3 }}>{item.title}</Typography>
                      <Typography variant="caption" sx={{ color: "#78909C", lineHeight: 1.6 }}>{item.desc}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ── TAB 2: Poll Builder ─────────────────────────────────────────────── */}
      {tab === 2 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", mb: 3 }}>
              <Box sx={{ px: 2.5, py: 2, background: "linear-gradient(135deg,#0F766E,#14B8A6)" }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <HowToVote sx={{ color: "#fff" }} />
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Poll Builder</Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,.7)" }}>Create an interactive poll — followers vote and see live results</Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                {pollAlert && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setPollAlert("")}>{pollAlert}</Alert>}
                <Stack spacing={2}>
                  <TextField label="Poll question *" placeholder="e.g. Which deworming protocol do you prefer in small dogs?" value={pollQuestion} onChange={(e) => setPollQuestion(e.target.value)} fullWidth size="small" multiline rows={2} />
                  <Typography sx={{ fontWeight: 700, fontSize: ".85rem", color: "#455A64" }}>
                    Answer options <Typography component="span" variant="caption" sx={{ color: "#78909C" }}>(set preset % to simulate realistic vote distribution)</Typography>
                  </Typography>
                  {pollOpts.map((opt, i) => (
                    <Stack key={opt.id} direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 26, height: 26, borderRadius: "7px", bgcolor: ["#EEF4FF","#F0FDF4","#FFFBEB","#FAF5FF"][i % 4], border: `1px solid ${["#BFDBFE","#A7F3D0","#FDE68A","#DDD6FE"][i % 4]}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: ".75rem", color: ["#1565C0","#065F46","#92400E","#5B21B6"][i % 4] }}>{String.fromCharCode(65 + i)}</Typography>
                      </Box>
                      <TextField placeholder={`Option ${String.fromCharCode(65 + i)}`} value={opt.text} onChange={(e) => updatePollOption(opt.id, "text", e.target.value)} size="small" sx={{ flex: 1 }} />
                      <TextField label="% votes" value={opt.votes} onChange={(e) => updatePollOption(opt.id, "votes", e.target.value)} size="small" sx={{ width: 80 }} type="number" inputProps={{ min: 0, max: 100 }} />
                      {pollOpts.length > 2 && (
                        <IconButton size="small" onClick={() => removePollOption(opt.id)} sx={{ color: "#CBD5E1", "&:hover": { color: "#EF4444" } }}>
                          <Delete sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      )}
                    </Stack>
                  ))}
                  {pollOpts.length < 4 && <Button size="small" startIcon={<Add fontSize="small" />} onClick={addPollOption} sx={{ textTransform: "none", color: "#0F766E", fontSize: ".82rem", alignSelf: "flex-start" }}>Add option</Button>}
                  <FormControl size="small" fullWidth>
                    <InputLabel>CTA links to</InputLabel>
                    <Select label="CTA links to" value={pollLink} onChange={(e) => setPollLink(e.target.value)}>
                      {PAGE_LINKS.map((pl) => <MenuItem key={pl.path} value={pl.path}>{pl.label} — {`${DOMAIN}${pl.path}`}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Button variant="contained" startIcon={<Visibility />} onClick={handleGeneratePoll} sx={{ bgcolor: "#0F766E", "&:hover": { bgcolor: "#0D6B63" }, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>Generate Poll Link</Button>
                </Stack>
              </Box>
            </Paper>
            {pollUrl && (
              <Paper sx={{ borderRadius: "14px", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,.06)" }}>
                <Box sx={{ px: 2, py: 1.5, bgcolor: "#0F766E", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".88rem" }}>Shareable Poll Link</Typography>
                  <CopyButton text={pollUrl} />
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography variant="caption" sx={{ color: "#0F766E", wordBreak: "break-all", lineHeight: 1.6 }}>{pollUrl}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button size="small" startIcon={<OpenInNew fontSize="small" />} onClick={() => window.open(pollUrl, "_blank")} sx={{ textTransform: "none", color: "#0F766E", fontSize: ".8rem" }}>Preview page</Button>
                    <Button size="small" startIcon={<Refresh fontSize="small" />} onClick={() => { setPollUrl(""); setPollQuestion(""); setPollOpts([{ id: uid(), text: "", votes: "50" }, { id: uid(), text: "", votes: "30" }]); }} sx={{ textTransform: "none", color: "#78909C", fontSize: ".8rem" }}>Clear</Button>
                  </Stack>
                </Box>
              </Paper>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", position: "sticky", top: 20 }}>
              <Box sx={{ px: 2.5, py: 2, bgcolor: "#F0FDF4" }}>
                <Typography sx={{ fontWeight: 700, color: "#0A1628", fontSize: ".95rem" }}>🗳️ How to use Polls</Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                {[
                  { step: "1", title: "Write an engaging question",  desc: "Clinical dilemmas work best: 'Which treatment would you choose?' or 'What's the first sign of X?'" },
                  { step: "2", title: "Set realistic preset votes",  desc: "Pre-set vote %s so results look realistic immediately. Make the correct answer around 40–55%." },
                  { step: "3", title: "Share the link",              desc: "Post in Instagram bio, Stories, or WhatsApp. Followers click, vote, and see results instantly." },
                  { step: "4", title: "Viral sharing built in",      desc: "After voting, followers get a 'Share My Vote' button — driving organic reach." },
                ].map((item) => (
                  <Box key={item.step} sx={{ display: "flex", gap: 2, mb: 2.5, "&:last-child": { mb: 0 } }}>
                    <Box sx={{ width: 26, height: 26, borderRadius: "8px", bgcolor: "#0F766E", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: 0.2 }}>
                      <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: ".78rem" }}>{item.step}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: ".88rem", color: "#0A1628", mb: 0.3 }}>{item.title}</Typography>
                      <Typography variant="caption" sx={{ color: "#78909C", lineHeight: 1.6 }}>{item.desc}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ── TAB 3: Case Builder ─────────────────────────────────────────────── */}
      {tab === 3 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", mb: 3 }}>
              <Box sx={{ px: 2.5, py: 2, background: "linear-gradient(135deg,#B91C1C,#F97316)" }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <MedicalServices sx={{ color: "#fff" }} />
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Case Builder</Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,.7)" }}>Create a clinical case challenge — followers diagnose and learn</Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                {caseAlert && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setCaseAlert("")}>{caseAlert}</Alert>}
                <Stack spacing={2}>
                  <TextField label="Case Title *" placeholder="e.g. Emergency Presentation — GDV Suspect" value={caseTitle} onChange={(e) => setCaseTitle(e.target.value)} fullWidth size="small" />
                  <TextField label="Patient Scenario *" placeholder="e.g. A 5-year-old male German Shepherd presents with acute abdominal distension..." value={caseScenario} onChange={(e) => setCaseScenario(e.target.value)} fullWidth size="small" multiline rows={4} helperText="Describe the patient, history, clinical signs, and key findings" />
                  <TextField label="Diagnostic Question *" placeholder="e.g. What is the most likely diagnosis and immediate next step?" value={caseQuestion} onChange={(e) => setCaseQuestion(e.target.value)} fullWidth size="small" />
                  <Typography sx={{ fontWeight: 700, fontSize: ".85rem", color: "#455A64" }}>Answer Options <Typography component="span" variant="caption" sx={{ color: "#78909C" }}>(select the correct one)</Typography></Typography>
                  <RadioGroup value={String(caseAnswer)} onChange={(e) => setCaseAnswer(Number(e.target.value))}>
                    {caseOpts.map((opt, i) => (
                      <Stack key={i} direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <FormControlLabel value={String(i)} control={<Radio size="small" sx={{ color: "#78909C", "&.Mui-checked": { color: "#22C55E" } }} />} label="" sx={{ mr: 0, minWidth: 0 }} />
                        <Box sx={{ width: 26, height: 26, borderRadius: "6px", flexShrink: 0, bgcolor: caseAnswer === i ? "#DCFCE7" : "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Typography sx={{ fontWeight: 800, fontSize: ".78rem", color: caseAnswer === i ? "#16A34A" : "#64748B" }}>{OPTION_LETTERS[i]}</Typography>
                        </Box>
                        <TextField placeholder={`Option ${OPTION_LETTERS[i]}`} value={opt} onChange={(e) => updateCaseOpt(i, e.target.value)} size="small" sx={{ flex: 1, "& .MuiOutlinedInput-root": { bgcolor: caseAnswer === i ? "#F0FDF4" : undefined, borderRadius: "8px" } }} />
                      </Stack>
                    ))}
                  </RadioGroup>
                  <TextField label="Clinical Explanation *" placeholder="e.g. GDV is life-threatening and requires immediate gastric decompression..." value={caseExplanation} onChange={(e) => setCaseExplanation(e.target.value)} fullWidth size="small" multiline rows={3} helperText="Shown after the follower answers — makes the case educational and shareable" />
                  <FormControl size="small" fullWidth>
                    <InputLabel>CTA links to</InputLabel>
                    <Select label="CTA links to" value={caseLink} onChange={(e) => setCaseLink(e.target.value)}>
                      {PAGE_LINKS.map((pl) => <MenuItem key={pl.path} value={pl.path}>{pl.label} — {`${DOMAIN}${pl.path}`}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Button variant="contained" startIcon={<Visibility />} onClick={handleGenerateCase} sx={{ bgcolor: "#B91C1C", "&:hover": { bgcolor: "#991B1B" }, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>Generate Case Link</Button>
                </Stack>
              </Box>
            </Paper>
            {caseUrl && (
              <Paper sx={{ borderRadius: "14px", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,.06)" }}>
                <Box sx={{ px: 2, py: 1.5, bgcolor: "#B91C1C", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".88rem" }}>Shareable Case Link</Typography>
                  <CopyButton text={caseUrl} />
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography variant="caption" sx={{ color: "#B91C1C", wordBreak: "break-all", lineHeight: 1.6 }}>{caseUrl}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button size="small" startIcon={<OpenInNew fontSize="small" />} onClick={() => window.open(caseUrl, "_blank")} sx={{ textTransform: "none", color: "#B91C1C", fontSize: ".8rem" }}>Preview page</Button>
                    <Button size="small" startIcon={<Refresh fontSize="small" />} onClick={() => { setCaseUrl(""); setCaseTitle(""); setCaseScenario(""); setCaseQuestion(""); setCaseOpts(["","","",""]); setCaseAnswer(0); setCaseExplanation(""); }} sx={{ textTransform: "none", color: "#78909C", fontSize: ".8rem" }}>Clear</Button>
                  </Stack>
                </Box>
              </Paper>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", position: "sticky", top: 20 }}>
              <Box sx={{ px: 2.5, py: 2, bgcolor: "#FEF2F2" }}>
                <Typography sx={{ fontWeight: 700, color: "#0A1628", fontSize: ".95rem" }}>🩺 How to use Case Challenges</Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                {[
                  { step: "1", title: "Write a real scenario",       desc: "Use a case you've seen or a classic exam case. Include species, breed, age, signs, and key findings." },
                  { step: "2", title: "Add 4 plausible options",     desc: "Make the 3 wrong answers believable — this is what makes the challenge engaging and educational." },
                  { step: "3", title: "Write a clinical explanation", desc: "The post-answer explanation is the most-shared part. Include pathophysiology and clinical pearls." },
                  { step: "4", title: "Post weekly as #DrPawCase",   desc: "Share every Tuesday. Reshare correct answers on Stories Friday." },
                ].map((item) => (
                  <Box key={item.step} sx={{ display: "flex", gap: 2, mb: 2.5, "&:last-child": { mb: 0 } }}>
                    <Box sx={{ width: 26, height: 26, borderRadius: "8px", bgcolor: "#B91C1C", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: 0.2 }}>
                      <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: ".78rem" }}>{item.step}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: ".88rem", color: "#0A1628", mb: 0.3 }}>{item.title}</Typography>
                      <Typography variant="caption" sx={{ color: "#78909C", lineHeight: 1.6 }}>{item.desc}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ── TAB 4: Countdown Builder ────────────────────────────────────────── */}
      {tab === 4 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", mb: 3 }}>
              <Box sx={{ px: 2.5, py: 2, background: "linear-gradient(135deg,#7C3AED,#A855F7)" }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Timer sx={{ color: "#fff" }} />
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Countdown Builder</Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,.7)" }}>Build launch urgency — capture leads before a course or event opens</Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                {cdAlert && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setCdAlert("")}>{cdAlert}</Alert>}
                <Stack spacing={2}>
                  <TextField label="Launch Title *" placeholder="e.g. New Masterclass Launching Soon" value={cdTitle} onChange={(e) => setCdTitle(e.target.value)} fullWidth size="small" />
                  <TextField label="Subtitle (optional)" placeholder="e.g. Advanced Small Animal Surgery — 8 weeks" value={cdSubtitle} onChange={(e) => setCdSubtitle(e.target.value)} fullWidth size="small" />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField label="Emoji" placeholder="🎓" value={cdEmoji} onChange={(e) => setCdEmoji(e.target.value)} size="small" sx={{ width: 90 }} />
                    <TextField label="Badge text (optional)" placeholder="e.g. Only 20 spots" value={cdBadge} onChange={(e) => setCdBadge(e.target.value)} size="small" sx={{ flex: 1 }} />
                  </Stack>
                  <TextField label="CTA Button text" placeholder="e.g. Secure My Spot" value={cdCta} onChange={(e) => setCdCta(e.target.value)} fullWidth size="small" />
                  <TextField label="Launch Date & Time *" type="datetime-local" value={cdDate} onChange={(e) => setCdDate(e.target.value)} fullWidth size="small" InputLabelProps={{ shrink: true }} helperText="When the countdown reaches zero the page switches to 'Enrollment is OPEN!'" />
                  <FormControl size="small" fullWidth>
                    <InputLabel>CTA links to</InputLabel>
                    <Select label="CTA links to" value={cdLink} onChange={(e) => setCdLink(e.target.value)}>
                      {PAGE_LINKS.map((pl) => <MenuItem key={pl.path} value={pl.path}>{pl.label} — {`${DOMAIN}${pl.path}`}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Button variant="contained" startIcon={<Visibility />} onClick={handleGenerateCountdown} sx={{ background: "linear-gradient(135deg,#7C3AED,#A855F7)", "&:hover": { background: "linear-gradient(135deg,#6D28D9,#9333EA)" }, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>Generate Countdown Link</Button>
                </Stack>
              </Box>
            </Paper>
            {cdUrl && (
              <Paper sx={{ borderRadius: "14px", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,.06)" }}>
                <Box sx={{ px: 2, py: 1.5, background: "linear-gradient(135deg,#7C3AED,#A855F7)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".88rem" }}>Shareable Countdown Link</Typography>
                  <CopyButton text={cdUrl} />
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography variant="caption" sx={{ color: "#7C3AED", wordBreak: "break-all", lineHeight: 1.6 }}>{cdUrl}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button size="small" startIcon={<OpenInNew fontSize="small" />} onClick={() => window.open(cdUrl, "_blank")} sx={{ textTransform: "none", color: "#7C3AED", fontSize: ".8rem" }}>Preview page</Button>
                    <Button size="small" startIcon={<Refresh fontSize="small" />} onClick={() => { setCdUrl(""); setCdTitle(""); setCdSubtitle(""); setCdBadge(""); setCdDate(""); }} sx={{ textTransform: "none", color: "#78909C", fontSize: ".8rem" }}>Clear</Button>
                  </Stack>
                </Box>
              </Paper>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", mb: 3 }}>
              <Box sx={{ px: 2.5, py: 2, background: "linear-gradient(135deg,#7C3AED,#A855F7)" }}>
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Real Examples</Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,.8)" }}>Click to load an example into the builder</Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                {COUNTDOWN_EXAMPLES.map((ex, i) => (
                  <Box key={i} onClick={() => handleLoadCountdownExample(ex)} sx={{ p: 2, mb: i < COUNTDOWN_EXAMPLES.length - 1 ? 1.5 : 0, borderRadius: "12px", border: "1px solid #E8EDF5", cursor: "pointer", transition: "all .2s", "&:hover": { borderColor: "#7C3AED", bgcolor: "#FAF5FF" } }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.6 }}>
                      <Typography sx={{ fontSize: "1.2rem" }}>{ex.emoji}</Typography>
                      <Chip label={ex.tag} size="small" sx={{ fontSize: ".7rem", fontWeight: 700, bgcolor: "rgba(124,58,237,.1)", color: "#7C3AED" }} />
                    </Stack>
                    <Typography sx={{ fontWeight: 700, fontSize: ".85rem", color: "#0A1628", mb: 0.3 }}>{ex.title}</Typography>
                    <Typography variant="caption" sx={{ color: "#78909C", display: "block", mb: 0.4 }}>{ex.subtitle}</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                      <Chip label={`Badge: ${ex.badge}`} size="small" sx={{ fontSize: ".68rem", bgcolor: "#F5F3FF", color: "#5B21B6" }} />
                      <Chip label={`In ${ex.daysFromNow} days`} size="small" sx={{ fontSize: ".68rem", bgcolor: "#F0FDF4", color: "#065F46" }} />
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ── TAB 5: Leaderboard Builder ──────────────────────────────────────── */}
      {tab === 5 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", mb: 3 }}>
              <Box sx={{ px: 2.5, py: 2, background: "linear-gradient(135deg,#D97706,#FBBF24)" }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <EmojiEvents sx={{ color: "#fff" }} />
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Leaderboard Builder</Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,.8)" }}>Showcase top quiz performers — drives competition and more attempts</Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                {lbAlert && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setLbAlert("")}>{lbAlert}</Alert>}
                <Stack spacing={2}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField label="Quiz Title *" placeholder="e.g. Weekly Pharmacology Challenge" value={lbQuiz} onChange={(e) => setLbQuiz(e.target.value)} size="small" sx={{ flex: 1 }} />
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Period</InputLabel>
                      <Select label="Period" value={lbPeriod} onChange={(e) => setLbPeriod(e.target.value)}>
                        {["This Week","This Month","All Time","Today"].map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Stack>
                  <Typography sx={{ fontWeight: 700, fontSize: ".85rem", color: "#455A64" }}>
                    Leaderboard Entries <Typography component="span" variant="caption" sx={{ color: "#78909C" }}>(name, score 0–100, optional flag emoji)</Typography>
                  </Typography>
                  {lbEntries.map((entry, i) => (
                    <Stack key={entry.id} direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 32, height: 32, borderRadius: "8px", flexShrink: 0, bgcolor: i === 0 ? "#FEF3C7" : i === 1 ? "#F1F5F9" : i === 2 ? "#FEF2F2" : "#F8FAFF", border: `1px solid ${i === 0 ? "#FDE68A" : i === 1 ? "#CBD5E1" : i === 2 ? "#FECACA" : "#E8EDF5"}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography sx={{ fontSize: i < 3 ? "1rem" : ".75rem", fontWeight: 800, color: i < 3 ? undefined : "#94A3B8" }}>{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `#${i + 1}`}</Typography>
                      </Box>
                      <TextField placeholder="Dr. Name" value={entry.name} onChange={(e) => updateLbEntry(entry.id, "name", e.target.value)} size="small" sx={{ flex: 1 }} />
                      <TextField label="Score %" value={entry.score} onChange={(e) => updateLbEntry(entry.id, "score", e.target.value)} size="small" sx={{ width: 85 }} type="number" inputProps={{ min: 0, max: 100 }} />
                      <TextField label="Flag" placeholder="🇱🇧" value={entry.flag} onChange={(e) => updateLbEntry(entry.id, "flag", e.target.value)} size="small" sx={{ width: 72 }} />
                      {lbEntries.length > 2 && (
                        <IconButton size="small" onClick={() => removeLbEntry(entry.id)} sx={{ color: "#CBD5E1", "&:hover": { color: "#EF4444" } }}>
                          <Delete sx={{ fontSize: "1rem" }} />
                        </IconButton>
                      )}
                    </Stack>
                  ))}
                  {lbEntries.length < 10 && <Button size="small" startIcon={<Add fontSize="small" />} onClick={addLbEntry} sx={{ textTransform: "none", color: "#D97706", fontSize: ".82rem", alignSelf: "flex-start" }}>Add entry</Button>}
                  <TextField label="Link back to quiz (optional)" placeholder={`${DOMAIN}/quiz?data=...`} value={lbLink} onChange={(e) => setLbLink(e.target.value)} fullWidth size="small" helperText="Paste the quiz link — the 'Beat the score' button will point here" />
                  <Button variant="contained" startIcon={<Visibility />} onClick={handleGenerateLeaderboard} sx={{ background: "linear-gradient(135deg,#D97706,#F59E0B)", "&:hover": { background: "linear-gradient(135deg,#B45309,#D97706)" }, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>Generate Leaderboard Link</Button>
                </Stack>
              </Box>
            </Paper>
            {lbUrl && (
              <Paper sx={{ borderRadius: "14px", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,.06)" }}>
                <Box sx={{ px: 2, py: 1.5, background: "linear-gradient(135deg,#D97706,#F59E0B)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".88rem" }}>Shareable Leaderboard Link</Typography>
                  <CopyButton text={lbUrl} />
                </Box>
                <Box sx={{ p: 2 }}>
                  <Typography variant="caption" sx={{ color: "#D97706", wordBreak: "break-all", lineHeight: 1.6 }}>{lbUrl}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button size="small" startIcon={<OpenInNew fontSize="small" />} onClick={() => window.open(lbUrl, "_blank")} sx={{ textTransform: "none", color: "#D97706", fontSize: ".8rem" }}>Preview page</Button>
                    <Button size="small" startIcon={<Refresh fontSize="small" />} onClick={() => { setLbUrl(""); setLbQuiz(""); setLbEntries([{ id: uid(), name: "", score: "", flag: "" }, { id: uid(), name: "", score: "", flag: "" }, { id: uid(), name: "", score: "", flag: "" }]); }} sx={{ textTransform: "none", color: "#78909C", fontSize: ".8rem" }}>Clear</Button>
                  </Stack>
                </Box>
              </Paper>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper sx={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,.06)", mb: 3 }}>
              <Box sx={{ px: 2.5, py: 2, background: "linear-gradient(135deg,#D97706,#FBBF24)" }}>
                <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: ".95rem" }}>Real Examples</Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,.8)" }}>Click to load into the builder</Typography>
              </Box>
              <Box sx={{ p: 2 }}>
                {LEADERBOARD_EXAMPLES.map((ex, i) => (
                  <Box key={i} onClick={() => handleLoadLeaderboardExample(ex)} sx={{ p: 2, mb: i < LEADERBOARD_EXAMPLES.length - 1 ? 1.5 : 0, borderRadius: "12px", border: "1px solid #E8EDF5", cursor: "pointer", transition: "all .2s", "&:hover": { borderColor: "#D97706", bgcolor: "#FFFBEB" } }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.8 }}>
                      <Chip label={ex.tag} size="small" sx={{ fontSize: ".7rem", fontWeight: 700, bgcolor: "rgba(217,119,6,.1)", color: "#B45309" }} />
                      <Chip label={ex.period} size="small" sx={{ fontSize: ".68rem", bgcolor: "#FFFBEB", color: "#92400E" }} />
                    </Stack>
                    <Typography sx={{ fontWeight: 700, fontSize: ".85rem", color: "#0A1628", mb: 0.8 }}>{ex.quiz}</Typography>
                    <Stack spacing={0.4}>
                      {ex.entries.slice(0, 3).map((e, ei) => (
                        <Stack key={ei} direction="row" alignItems="center" spacing={1}>
                          <Typography sx={{ fontSize: ".85rem" }}>{ei === 0 ? "🥇" : ei === 1 ? "🥈" : "🥉"}</Typography>
                          <Typography variant="caption" sx={{ flex: 1, color: "#455A64", fontWeight: ei === 0 ? 700 : 400 }}>{e.name} {e.flag}</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: "#D97706" }}>{e.score}%</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

    </Box>
  );
};

export default AdminMarketingEngagement;
